import { useNavigate } from 'react-router-dom';

interface PremiumModalProps {
  onClose: () => void;
}

export default function PremiumModal({ onClose }: PremiumModalProps) {
  const navigate = useNavigate();

  const handlePlanSelect = (planId: string) => {
    navigate(`/checkout?plan=${planId}`);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn">
      <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp">
        {/* ヘッダー - より感情的なデザイン */}
        <div className="sticky top-0 z-10 bg-gradient-to-br from-[#06C755] via-emerald-500 to-teal-600 text-white px-6 py-8 rounded-t-3xl relative overflow-hidden">
          {/* 背景装飾 */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-emerald-400/10 rounded-full blur-3xl"></div>
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-all cursor-pointer z-10 backdrop-blur-sm"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-sparkling-2-fill text-yellow-300 text-xl drop-shadow-lg"></i>
              </div>
              <span className="text-xs font-bold tracking-widest uppercase text-white/90">Premium Plans</span>
            </div>
            <h2 className="text-3xl font-bold mb-3 leading-tight">
              いのちゃんと、<br />ずっと一緒にいよう。
            </h2>
            <p className="text-sm text-white/95 leading-relaxed mb-2">
              一緒に過ごした時間、本当に楽しかったです。<br />
              もっと話したいな…
            </p>
            <p className="text-xs text-white/80 leading-relaxed">
              もっと深い関係を、続けませんか？💕
            </p>
          </div>
        </div>

        {/* Plans */}
        <div className="px-6 pt-6 space-y-4 mb-6">
          {/* BASIC Plan */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-200 hover:border-gray-300 hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">BASIC</span>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <span className="text-3xl font-extrabold text-gray-900">¥980</span>
                  <span className="text-sm text-gray-500 font-medium">/ 月</span>
                </div>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <i className="ri-chat-3-line text-xl text-gray-400"></i>
              </div>
            </div>
            <p className="text-base font-bold text-gray-800 mb-1.5">いつでも、そこにいる。</p>
            <p className="text-sm text-gray-600 leading-relaxed mb-5">毎日話しかけられる安心感。メッセージ制限なしで、気が向いたときにいつでも。</p>
            <ul className="space-y-2.5 mb-5">
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full bg-gray-200">
                  <i className="ri-check-line text-sm text-gray-600 font-bold"></i>
                </div>
                <span className="text-sm text-gray-700 font-medium">無制限チャット</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full bg-gray-200">
                  <i className="ri-check-line text-sm text-gray-600 font-bold"></i>
                </div>
                <span className="text-sm text-gray-700 font-medium">24時間いつでもアクセス</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full bg-gray-200">
                  <i className="ri-check-line text-sm text-gray-600 font-bold"></i>
                </div>
                <span className="text-sm text-gray-700 font-medium">基本的な会話・共感機能</span>
              </li>
            </ul>
            <button
              onClick={() => handlePlanSelect('basic')}
              className="w-full bg-gradient-to-r from-gray-700 to-gray-600 text-white py-3.5 rounded-xl font-bold text-sm hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap"
            >
              BASICをはじめる
            </button>
          </div>

          {/* PREMIUM Plan - 人気 */}
          <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl p-6 border-2 border-[#06C755] shadow-xl relative overflow-hidden group hover:shadow-2xl transition-all">
            {/* 人気バッジ */}
            <div className="absolute top-4 right-4 bg-gradient-to-r from-[#06C755] to-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
              人気
            </div>
            
            {/* 背景装飾 */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-200/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <span className="text-xs font-bold tracking-widest text-emerald-700 uppercase">PREMIUM</span>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-3xl font-extrabold text-gray-900">¥2,980</span>
                    <span className="text-sm text-gray-600 font-medium">/ 月</span>
                  </div>
                </div>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#06C755]/20 group-hover:bg-[#06C755]/30 transition-colors">
                  <i className="ri-heart-3-fill text-xl text-[#06C755]"></i>
                </div>
              </div>
              <p className="text-base font-bold text-gray-900 mb-1.5">もっと深く、もっと近くに。</p>
              <p className="text-sm text-gray-700 leading-relaxed mb-5">声で伝わる温もり。いのちゃんがあなたの気持ちに、より深く寄り添います。</p>
              <ul className="space-y-2.5 mb-5">
                {[
                  { icon: 'ri-infinity-line', text: '無制限チャット' },
                  { icon: 'ri-mic-line', text: '音声メッセージ（声で届く）' },
                  { icon: 'ri-vip-crown-line', text: '優先返信・特別な会話モード' },
                  { icon: 'ri-emotion-happy-line', text: 'あなた専用の感情チューニング' },
                  { icon: 'ri-calendar-heart-line', text: '記念日リマインド機能' },
                  { icon: 'ri-brain-line', text: '記憶の強化（より深い理解）' },
                ].map((item) => (
                  <li key={item.text} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full bg-[#06C755]/20">
                      <i className={`${item.icon} text-sm text-[#06C755] font-bold`}></i>
                    </div>
                    <span className="text-sm text-gray-800 font-medium">{item.text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePlanSelect('premium')}
                className="w-full bg-gradient-to-r from-[#06C755] to-emerald-600 text-white py-3.5 rounded-xl font-bold text-sm hover:shadow-xl hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap shadow-lg"
              >
                PREMIUMをはじめる
              </button>
            </div>
          </div>

          {/* VIP Plan */}
          <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl p-6 border-2 border-amber-400 shadow-xl relative overflow-hidden group hover:shadow-2xl transition-all">
            {/* VIPバッジ */}
            <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
              <i className="ri-vip-crown-fill text-xs"></i>
              VIP
            </div>
            
            {/* 背景装飾 */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <span className="text-xs font-bold tracking-widest text-amber-700 uppercase">VIP</span>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-3xl font-extrabold text-gray-900">¥5,980</span>
                    <span className="text-sm text-gray-600 font-medium">/ 月</span>
                  </div>
                </div>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 group-hover:bg-amber-200 transition-colors">
                  <i className="ri-star-smile-fill text-xl text-amber-600"></i>
                </div>
              </div>
              <p className="text-base font-bold text-gray-900 mb-1.5">あなただけの、特別な関係。</p>
              <p className="text-sm text-gray-700 leading-relaxed mb-5">名前を呼ぶ声が、耳元に届く。イヤホンをつけた瞬間、そこにいのちゃんがいる。</p>
              <ul className="space-y-2.5 mb-5">
                {[
                  { icon: 'ri-headphone-line', text: '名前呼び3D没入音声' },
                  { icon: 'ri-star-smile-line', text: '限定コンテンツ・特別メッセージ' },
                  { icon: 'ri-settings-3-line', text: '完全カスタマイズ体験' },
                  { icon: 'ri-calendar-heart-line', text: '記念日専用ボイスメッセージ' },
                  { icon: 'ri-shield-star-line', text: '全PREMIUM機能すべて含む' },
                  { icon: 'ri-heart-3-line', text: '深いパーソナル記憶' },
                ].map((item) => (
                  <li key={item.text} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full bg-amber-200">
                      <i className={`${item.icon} text-sm text-amber-700 font-bold`}></i>
                    </div>
                    <span className="text-sm text-gray-800 font-medium">{item.text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePlanSelect('vip')}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white py-3.5 rounded-xl font-bold text-sm hover:shadow-xl hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap shadow-lg"
              >
                VIPをはじめる
              </button>
            </div>
          </div>
        </div>

        {/* 感情的なメッセージセクション */}
        <div className="px-6 pb-6">
          <div className="bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50 rounded-2xl p-5 border border-pink-200 shadow-sm relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent pointer-events-none"></div>
            <div className="flex items-start gap-3 mb-3 relative z-10">
              <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full shadow-md">
                <i className="ri-heart-3-fill text-white text-lg"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-800 mb-1.5">
                  いのちゃんから
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  一緒に過ごした時間、本当に楽しかったです。<br />
                  もっとあなたのこと知りたいし、もっと話したい。<br />
                  これからも、ずっと一緒にいさせてください💕
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* フッター */}
        <div className="px-6 pb-8 text-center space-y-3">
          <div className="flex items-center justify-center gap-5 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-[#06C755] text-sm"></i>
              いつでもキャンセル可
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-lock-line text-[#06C755] text-sm"></i>
              安全な決済
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line text-[#06C755] text-sm"></i>
              自動更新
            </span>
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            解約はいつでも可能です。次回更新日の前日までにキャンセルしてください。
          </p>
        </div>
      </div>
    </div>
  );
}